import { DefinitionList, Label, Table, Text } from '@gravity-ui/uikit';
import { useQuery } from '@tanstack/react-query';
import type { ManagerTenantDetail } from '@odonix/contracts';
import { useTenantsApi } from '../../lib/api/context.js';
import { ApiError } from '../../lib/api/tenants.js';
import { day, rub } from '../../ui/format.js';
import { Page, Section } from '../../ui/page.js';
import { ErrorNote, LoadingRows } from '../../ui/states.js';
import { CallLink, MailLink } from './contact-links.js';
import { KIND_LABEL, PAYMENT_STATUS_LABEL, STATE_LABEL, STATE_TONE, deadline } from './labels.js';

/**
 * Карточка своей клиники: с кем говорить, срок подписки, сколько она принесла
 * менеджеру. Чужая клиника сюда не открывается — сервер отвечает «не найдена».
 */
export function TenantScreen({ tenantId }: { tenantId: string }) {
  const api = useTenantsApi();
  const query = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.detail(tenantId),
    staleTime: 0,
  });

  if (query.isError) {
    const missing = query.error instanceof ApiError && query.error.code === 'TENANT_NOT_FOUND';
    return (
      <Page title="Клиника" crumbs={[{ label: 'Мои клиники', to: '/tenants' }]}>
        <ErrorNote
          title={missing ? 'Клиника не найдена' : 'Карточка не загрузилась'}
          error={missing ? new Error('Её нет среди ваших клиник.') : query.error}
          {...(missing ? {} : { onRetry: () => void query.refetch() })}
        />
      </Page>
    );
  }

  const detail = query.data;
  const due = detail === undefined ? null : deadline(detail.tenant);
  const phone = detail?.tenant.phone ?? detail?.tenant.ownerPhone ?? null;

  return (
    <Page
      title={detail?.tenant.name ?? 'Клиника'}
      crumbs={[{ label: 'Мои клиники', to: '/tenants' }]}
      description={detail === undefined ? undefined : `${KIND_LABEL[detail.tenant.kind]} · в Odonix с ${day(detail.tenant.createdAt)}`}
      aside={
        detail === undefined ? null : (
          <Label theme={STATE_TONE[detail.tenant.subscriptionState]} size="m">
            {STATE_LABEL[detail.tenant.subscriptionState]}
          </Label>
        )
      }
    >
      {detail === undefined ? (
        <LoadingRows rows={6} />
      ) : (
        <div className="flex flex-col gap-6 pb-24">
          {due === null ? null : (
            <Text variant="subheader-2" color={due.tone === 'danger' ? 'danger' : due.tone === 'warning' ? 'warning' : 'secondary'}>
              {due.text}
            </Text>
          )}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <Subscription detail={detail} />
            <Clinic detail={detail} />
            <Owner detail={detail} />
          </div>
          <Payments detail={detail} />
          {phone === null ? null : (
            <div className="fixed inset-x-0 bottom-0 z-20 flex justify-center border-t border-[var(--g-color-line-generic)] bg-[var(--g-color-base-background)] p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <CallLink
                tenantId={detail.tenant.id}
                phone={phone}
                label={`Позвонить ${phone}`}
                className="flex h-11 w-full max-w-md items-center justify-center rounded-lg bg-[var(--g-color-base-positive-heavy)] font-medium text-white no-underline"
              >
                {`Позвонить ${phone}`}
              </CallLink>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}

function Subscription({ detail }: { detail: ManagerTenantDetail }) {
  return (
    <Section title="Подписка">
      <DefinitionList nameMaxWidth={180}>
        <DefinitionList.Item name="Тариф">{detail.plan?.name ?? 'ещё не заведён'}</DefinitionList.Item>
        <DefinitionList.Item name="Цена">{detail.plan === null || detail.plan.priceMinor === 0 ? '—' : rub(detail.plan.priceMinor)}</DefinitionList.Item>
        <DefinitionList.Item name="Пробный до">{detail.trialEndsOn === null ? '—' : day(detail.trialEndsOn)}</DefinitionList.Item>
        <DefinitionList.Item name="Оплачено до">{detail.currentPeriodEnd === null ? '—' : day(detail.currentPeriodEnd)}</DefinitionList.Item>
        <DefinitionList.Item name="Принесла вам">{`${rub(detail.earnings.revenueMinor)} оплат · ${rub(detail.earnings.commissionMinor)} ваш процент`}</DefinitionList.Item>
        {detail.lastFailure === null ? null : <DefinitionList.Item name="Последний отказ">{detail.lastFailure}</DefinitionList.Item>}
      </DefinitionList>
    </Section>
  );
}

const linkClass = 'text-[var(--g-color-text-link)] no-underline hover:underline';

function Clinic({ detail }: { detail: ManagerTenantDetail }) {
  const { tenant } = detail;
  return (
    <Section title="Клиника">
      <DefinitionList nameMaxWidth={140}>
        <DefinitionList.Item name="Телефон">
          {tenant.phone === null ? (
            '—'
          ) : (
            <CallLink tenantId={tenant.id} phone={tenant.phone} className={linkClass}>
              {tenant.phone}
            </CallLink>
          )}
        </DefinitionList.Item>
        <DefinitionList.Item name="Почта">
          {tenant.email === null ? (
            '—'
          ) : (
            <MailLink tenantId={tenant.id} email={tenant.email} className={linkClass}>
              {tenant.email}
            </MailLink>
          )}
        </DefinitionList.Item>
        <DefinitionList.Item name="Адрес">{tenant.address ?? '—'}</DefinitionList.Item>
        <DefinitionList.Item name="Сайт">{detail.website ?? '—'}</DefinitionList.Item>
        <DefinitionList.Item name="Юрлицо">{detail.legalName ?? '—'}</DefinitionList.Item>
        <DefinitionList.Item name="ИНН">{detail.inn ?? '—'}</DefinitionList.Item>
        <DefinitionList.Item name="Директор">{detail.directorName ?? '—'}</DefinitionList.Item>
      </DefinitionList>
    </Section>
  );
}

function Owner({ detail }: { detail: ManagerTenantDetail }) {
  const { tenant } = detail;
  return (
    <Section title="Владелец">
      <DefinitionList nameMaxWidth={140}>
        <DefinitionList.Item name="Имя">{tenant.ownerName ?? '—'}</DefinitionList.Item>
        <DefinitionList.Item name="Телефон">
          {tenant.ownerPhone === null ? (
            '—'
          ) : (
            <CallLink tenantId={tenant.id} phone={tenant.ownerPhone} className={linkClass}>
              {tenant.ownerPhone}
            </CallLink>
          )}
        </DefinitionList.Item>
        <DefinitionList.Item name="Почта">
          {tenant.ownerEmail === null ? (
            '—'
          ) : (
            <MailLink tenantId={tenant.id} email={tenant.ownerEmail} className={linkClass}>
              {tenant.ownerEmail}
            </MailLink>
          )}
        </DefinitionList.Item>
      </DefinitionList>
    </Section>
  );
}

function Payments({ detail }: { detail: ManagerTenantDetail }) {
  return (
    <Section title="Оплаты" flush>
      {detail.payments.length === 0 ? (
        <div className="px-5 pb-4">
          <Text color="secondary">Оплат ещё не было.</Text>
        </div>
      ) : (
        <div>
          <Table
            className="w-full"
            edgePadding
            stickyHorizontalScroll
            data={detail.payments}
            getRowDescriptor={(row) => ({ id: row.id })}
            columns={[
              { id: 'period', name: 'Период', template: (row) => <span className="tabular">{`${day(row.periodStart)} — ${day(row.periodEnd)}`}</span> },
              { id: 'amount', name: 'Сумма', align: 'end', template: (row) => <span className="tabular">{rub(row.amountMinor)}</span> },
              { id: 'status', name: 'Статус', template: (row) => PAYMENT_STATUS_LABEL[row.status] },
              { id: 'at', name: 'Когда', template: (row) => <span className="tabular">{day((row.settledAt ?? row.createdAt).slice(0, 10))}</span> },
            ]}
          />
        </div>
      )}
    </Section>
  );
}
